/* eslint-disable @typescript-eslint/no-explicit-any */
// Lightweight fetch wrapper that logs requests and stores recent logs on window.__apiLogs
const inflightMap = new Map<string, Promise<Response>>(); // key -> Promise<Response>

export async function loggedFetch(input: RequestInfo | URL, init?: (RequestInit & { dedupe?: boolean }) | undefined): Promise<Response> {
  // Determine HTTP method: prefer init.method, then Request.method if provided, otherwise default to GET
  let method = 'GET';
  if (init && init.method) method = String(init.method).toUpperCase();
  else if (typeof input !== 'string' && 'method' in input && (input as any).method) method = String((input as any).method).toUpperCase();

  // Determine URL string safely for Request, URL or string inputs
  const url = typeof input === 'string' ? input : ('url' in (input as any) ? String((input as any).url) : String(input));
  const key = `${method}::${url}`;

  const dedupe = !(init && (init as any).dedupe === false);
  if (method === 'GET' && dedupe && inflightMap.has(key)) {
    const existing = inflightMap.get(key);
    // Return a cloned Response for each concurrent consumer so callers can
    // independently call `.json()` / read the body without colliding.
    if (existing) return existing.then(res => res.clone());
  }

  const start = Date.now();

  const promise = (async () => {
    try {
      const res = await fetch(input, init);
      const duration = Date.now() - start;
      const entry = { method, url, status: res.status, duration, timestamp: new Date().toISOString() };
      try {
        if (typeof window !== 'undefined') {
          const w = window as any;
          w.__apiLogs = w.__apiLogs || [];
          w.__apiLogs.push(entry);
          if (w.__apiLogs.length > 200) w.__apiLogs.shift();
        }
      } catch { }
      if (method === 'GET') console.info(`[API] ${method} ${url} -> ${res.status} (${duration}ms)`);
      else console.debug(`[API] ${method} ${url} -> ${res.status} (${duration}ms)`);
      return res;
    } catch (err) {
      const duration = Date.now() - start;
      const entry = { method, url, status: 'ERR', duration, timestamp: new Date().toISOString(), error: String(err) };
      try { if (typeof window !== 'undefined') { const w = window as any; w.__apiLogs = w.__apiLogs || []; w.__apiLogs.push(entry); } } catch { }
      console.error(`[API] ${method} ${url} -> ERROR (${duration}ms)`, err);
      throw err;
    } finally {
      if (method === 'GET' && dedupe) {
        if (inflightMap.get(key)) inflightMap.delete(key);
      }
    }
  })();

  if (method === 'GET' && dedupe) inflightMap.set(key, promise);

  return promise;
}

export default loggedFetch;

// Safe helper to fetch and parse JSON. Returns `null` when the response is not OK,
// when parsing fails, or when the request was aborted. Throws for other network errors.
export async function fetchJson(input: RequestInfo | URL, init?: (RequestInit & { dedupe?: boolean }) | undefined): Promise<any | null> {
  try {
    const res = await loggedFetch(input, init);
    if (!res.ok) return null;
    try {
      return await res.json();
    } catch (err) {
      // JSON parse failed
      return null;
    }
  } catch (err: unknown) {
    const e = err as { name?: string } | undefined;
    if (e?.name === 'AbortError') {
      // Caller likely aborted due to unmount — return null silently
      return null;
    }
    // Propagate other errors so callers can handle/log them
    throw err;
  }
}
